import { getMsGraphUrl } from '@microsoft/portal-app/lib/ms-graph-api/getMsGraphUrl';
import { IAjaxRequest } from '@microsoft/portal-app/lib/auth';
import { getApiServerUrl } from '@microsoft/portal-app/lib/environment/getApiServerUrl';
import { ICheckMemberGroupsRequest } from '../../Privacy/redux/actions/ICheckMemberGroupsRequest';
import { getSubstrateSearchUrl } from './getSubstrateSearchUrl';
import { getSubstrateSearchAudienceName } from './getSubstrateSearchAudienceName';
import { getMSGraphScopeAndEndPoint } from '../../shared/utils';
import { MSGraphScopes } from '../../shared/constants';

const getGraphUrlV1 = getMsGraphUrl();
const getGraphUrlBeta = getMsGraphUrl('beta');
const getSubstrateSearchUrlV1 = getSubstrateSearchUrl('v1');

export const getMailboxSettingsUrl = () => getGraphUrlV1('/me/mailboxsettings').toString();
export const getPhotoUrl = () => getGraphUrlV1('/me/photos/648x648/$value').toString();
export const getMePhotoUrl = () => getGraphUrlV1('/me/photo/$value').toString();
export const getAvailableTimezonesUrl = (lang: string) =>
  getApiServerUrl('/api/allTimeZones').toString() + `?lang=${lang}`;
export const getAvailableLocalesUrl = () => getApiServerUrl('/api/availableRegionalFormats').toString();
export const getBatchUpdateUrl = () => getGraphUrlBeta('/$batch').toString();
export const getRegionalAndLanguageUrl = () => getGraphUrlBeta('/me/settings/regionalAndLanguageSettings').toString();
export const getUserItemInsightsSettingsUrl = () => getGraphUrlBeta('/me/settings/itemInsights').toString();
export const postCheckMemberGroupsUrl = () => getGraphUrlBeta(`/me/checkMemberGroups`).toString();
export const getContactMergeSuggestionsSettingsUrl = () =>
  getGraphUrlBeta('/me/settings/contactMergeSuggestions').toString();
export const getTenantBrandingLogoUrl = (orgId: string) =>
  getGraphUrlV1(
    `/organization/${orgId}/branding/localizations?`,
    '$select=id,cdnList,bannerLogoRelativeUrl',
  ).toString();
export const getOrgContactInsightsSettingsUrl = (orgId: string) =>
  getGraphUrlBeta(`/organization/${orgId}/settings/contactInsights`).toString();
export const getOrgItemInsightsSettingsUrl = (orgId: string) =>
  getGraphUrlBeta(`/organization/${orgId}/settings/itemInsights`).toString();
export const getOrgPeopleInsightsSettingsUrl = (orgId: string) =>
  getGraphUrlBeta(`/organization/${orgId}/settings/peopleInsights`).toString();
export const getAssignedPlansUrl = () => getGraphUrlV1('/me?', '$select=assignedPlans').toString();
export const getUserInfoUrl = () =>
  getGraphUrlV1(
    '/me?',
    '$select=displayName,userPrincipalName,businessPhones,officeLocation,city,mail,jobTitle,preferredLanguage,givenName,surname',
  ).toString();
// https://learn.microsoft.com/en-us/graph/api/rbacapplication-list-roleassignments?view=graph-rest-1.0&tabs=http
export const getRoleAssignmentsUrl = (userId: string) =>
  getGraphUrlV1(
    `/roleManagement/directory/roleAssignments?'`,
    `$filter = principalId eq '${encodeURIComponent(userId)}'`,
  ).toString();

export const getProfilePhotoAllowedOperationsUrl = () => getGraphUrlBeta('/me/photo/getAllowedOperations').toString();

// This extended property is used to store the display name of the source (e.g. mobile device) on each contact imported from that source.
// This is Customer Content and needs to be included in DSR exports for completeness.
export const extendedPropertyIdImportDisplayName = 'String 0x200106F';

// This extended property is used to store the contact's id on the source (e.g. mobile device).
// This information is imported from the source (i.e. device) and needs to be included in DSR exports for completeness.
export const extendedPropertyIdSourceContactId = 'String 0x2001081';

export const getImplicitContactsUrl = () =>
  getGraphUrlV1(
    "/me/contactFolders('ImplicitContacts')/contacts",
    '$select=DisplayName,EmailAddresses,BusinessPhones,HomePhones&$top=1000',
  ).toString();
export const getImportedContactsUrl = () =>
  getGraphUrlV1(
    '/me/contactFolders/ImportedContacts/contacts',
    `$top=1000&$expand=SingleValueExtendedProperties($filter=Id eq '${extendedPropertyIdImportDisplayName}' or Id eq '${extendedPropertyIdSourceContactId}')`,
  ).toString();

export interface IPreferredLanguage {
  value: string;
}
export const getDirSyncStatusAndLanguagePreferenceRequest = (): IAjaxRequest => ({
  method: 'GET',
  url: getGraphUrlBeta('/me?', '$select=onPremisesSyncEnabled,userPrincipalName,preferredLanguage').toString(),
  audience: getMSGraphScopeAndEndPoint(MSGraphScopes.UserReadAll).scope,
});

export interface IDirSyncStatusAndLanguagePreference {
  '@odata.context': string;
  onPremisesSyncEnabled: boolean | null;
  userPrincipalName: string;
  preferredLanguage: string | null;
}

export const getUserItemInsightsSettingsReq = (): IAjaxRequest => ({
  method: 'GET',
  url: getUserItemInsightsSettingsUrl(),
  audience: getMSGraphScopeAndEndPoint(MSGraphScopes.UserReadAll).scope,
});

export const getUserPeopleInsightsSettingsReq = (): IAjaxRequest => ({
  method: 'GET',
  url: getSubstrateSearchUrlV1(`/peopleinsightsusersettings`).toString(),
  audience: getSubstrateSearchAudienceName(),
});

export const postCheckMemberGroupsReq = (body: ICheckMemberGroupsRequest): IAjaxRequest => ({
  method: 'POST',
  url: postCheckMemberGroupsUrl(),
  audience: getMSGraphScopeAndEndPoint(MSGraphScopes.UserReadAll).scope,
  body: JSON.stringify(body),
});

export const getOrgItemInsightsSettingsReq = (orgId: string): IAjaxRequest => ({
  method: 'GET',
  url: getOrgItemInsightsSettingsUrl(orgId),
  audience: getMSGraphScopeAndEndPoint(MSGraphScopes.OrganizationReadAll).scope,
});

export const getOrgPeopleInsightsSettingsReq = (orgId: string): IAjaxRequest => ({
  method: 'GET',
  url: getOrgPeopleInsightsSettingsUrl(orgId),
  audience: getMSGraphScopeAndEndPoint(MSGraphScopes.OrganizationReadAll).scope,
});

type IItemInsightSettings = { isEnabled: boolean };
type Scenario = { Name: string };
type IPeopleInsightSettings = { isEnabled: boolean; Scenario: Scenario };
type IContactMergeSuggestionsSettings = { isEnabled: boolean };

export const setUserItemInsightsSettingsRequest = (settings: IItemInsightSettings): IAjaxRequest => ({
  method: 'PATCH',
  url: getUserItemInsightsSettingsUrl(),
  audience: getMSGraphScopeAndEndPoint(MSGraphScopes.UserReadWrite).scope,
  body: JSON.stringify(settings),
});

export const setUserPeopleInsightsSettingsRequest = (settings: IPeopleInsightSettings): IAjaxRequest => ({
  method: 'POST',
  url: getSubstrateSearchUrlV1(`/peopleinsightsusersettings`).toString(),
  audience: getSubstrateSearchAudienceName(),
  body: JSON.stringify(settings),
});

export const getContactMergeSuggestionsSettingsRequest = (): IAjaxRequest => ({
  method: 'GET',
  url: getContactMergeSuggestionsSettingsUrl(),
  audience: getMSGraphScopeAndEndPoint(MSGraphScopes.UserReadAll).scope,
});

export const setContactMergeSuggestionsSettingsRequest = (
  settings: IContactMergeSuggestionsSettings,
): IAjaxRequest => ({
  method: 'PATCH',
  url: getContactMergeSuggestionsSettingsUrl(),
  audience: getMSGraphScopeAndEndPoint(MSGraphScopes.UserReadWrite).scope,
  body: JSON.stringify(settings),
});

export const getOrgContactInsightsSettingsReq = (orgId: string): IAjaxRequest => ({
  method: 'GET',
  url: getOrgContactInsightsSettingsUrl(orgId),
  audience: getMSGraphScopeAndEndPoint(MSGraphScopes.OrganizationReadAll).scope,
});

export const getImplicitContactsRequest = (paginationUrl?: string | undefined | null): IAjaxRequest => ({
  method: 'GET',
  url: paginationUrl ?? getImplicitContactsUrl(),
  audience: getMSGraphScopeAndEndPoint(MSGraphScopes.ContactsRead).scope,
});

export const getImportedContactsRequest = (paginationUrl?: string | undefined | null): IAjaxRequest => ({
  method: 'GET',
  url: paginationUrl ?? getImportedContactsUrl(),
  audience: getMSGraphScopeAndEndPoint(MSGraphScopes.ContactsRead).scope,
});
